import { Board } from '../../../../lib/api/boards';

/**
 * Base interface for all chart components
 * All charts receive board data through the useBoardChartData hook
 */
export interface BaseChartProps {
    board?: Board;
    isEditMode?: boolean;
    [key: string]: unknown;
}

export type ChartCategory = 'Financial' | 'Decision' | 'Operations' | 'Marketing' | 'Customer Behavior' | 'Growth' | 'Risk' | 'Prediction' | 'Expert' | 'Other';

export type SizeTier = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface SizeTierConfig {
    min: number;
    preferred: number;
    max: number;
}

export const SIZE_TIERS: Record<SizeTier, SizeTierConfig> = {
    'xs': { min: 1, preferred: 2, max: 2 },
    'sm': { min: 3, preferred: 6, max: 8 },
    'md': { min: 4, preferred: 12, max: 12 },
    'lg': { min: 4, preferred: 16, max: 24 },
    'xl': { min: 4, preferred: 24, max: 24 }
};

/**
 * Explicit width lookup table: CHART_WIDTH_MAP[sizeTier][breakpoint] = grid columns.
 * Rows = chart size tier, Columns = screen breakpoint.
 *
 *          xs  sm  md  lg  xl
 *    xs     2   3   3   3   3
 *    sm     3   4   5   8   6
 *    md     4   8  12  12  12
 *    lg     4   8  12  16  16
 *    xl     4   8  12  24  24
 */
export const CHART_WIDTH_MAP: Record<SizeTier, Record<string, number>> = {
    'xs': { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 },
    'sm': { xs: 3, sm: 4, md: 5, lg: 8, xl: 6 },
    'md': { xs: 4, sm: 8, md: 12, lg: 12, xl: 12 },
    'lg': { xs: 4, sm: 8, md: 12, lg: 16, xl: 16 },
    'xl': { xs: 4, sm: 8, md: 12, lg: 24, xl: 24 },
};

/**
 * Default reference heights per size tier (in grid units).
 * At runtime, the displayed height is: min(referenceHeight, breakpointColumns).
 * This means charts keep their exact height unless the target screen's capacity
 * (column count) is smaller, in which case the height is capped.
 *
 * Examples (md tier, referenceHeight = 8):
 *   xs (4 cols) → min(8, 4)  = 4   (capped)
 *   sm (8 cols) → min(8, 8)  = 8   (exact fit)
 *   md (12 cols)→ min(8, 12) = 8   (fits)
 *   xl (24 cols)→ min(8, 24) = 8   (fits)
 */
export const DEFAULT_CHART_HEIGHTS: Record<SizeTier, number> = {
    'xs': 2,
    'sm': 4,
    'md': 8,
    'lg': 8,
    'xl': 12,
};

export interface BreakpointConfig {
    maxWidth: number;
    columns: number;
    minChartSize: number;
    maxHeightUnits: number; // Max visible vertical grid units (based on typical viewport height)
}

export const BREAKPOINTS: Record<string, BreakpointConfig> = {
    'xs': { maxWidth: 640, columns: 4, minChartSize: 1, maxHeightUnits: 22 },
    'sm': { maxWidth: 768, columns: 8, minChartSize: 1, maxHeightUnits: 22 },
    'md': { maxWidth: 1024, columns: 12, minChartSize: 1, maxHeightUnits: 22 },
    'lg': { maxWidth: 1280, columns: 24, minChartSize: 1, maxHeightUnits: 22 },
    'xl': { maxWidth: Infinity, columns: 24, minChartSize: 1, maxHeightUnits: 22 }
};

/**
 * Chart definition in the registry
 */
export interface ChartDefinition {
    type: string;
    title: string;
    description?: string;
    version: string; // Semantic versioning (e.g., "1.0.0")
    defaultSizeTier: SizeTier;
    defaultAspectRatio: string;
    icon?: React.ReactNode;
    categories: ChartCategory[];
    hasCustomHeader?: boolean;
    requiredData?: ("metrics" | "dailyBreakdown" | "trafficSummary" | "breakdown" | "eventsSummary" | "specs" | "transactions" | "detailedBreakdown")[];
}


/**
 * Schema for storing chart configuration in database/templates
 */
export interface StoredChartConfig {
    chartId: string;      // The type of the chart (e.g. 'transactions')
    instanceId: string;   // Unique ID for this instance
    chartName: string;    // Display name
    chartDescription?: string;
    chartVersion: string; // Version of the chart definition

    // New semantic layout fields
    sizeTier: SizeTier;
    order: number;
    aspectRatio: string;

    // Layout fields - critical for exact position restoration
    x?: number;
    y?: number;
    w?: number;
    h?: number;

    // Tracks which breakpoint the layout was last edited on (xl, lg, md, etc.)
    _editBp?: string;
}


/**
 * Chart data provided by useBoardChartData hook
 */
export interface BoardChartData {
    finance: any | null;
    events: any[] | null;
    dateRange: { startDate: Date | null; endDate: Date | null };
    currencySettings: any | null;
    selectedEntities: any[];
    isLoading: boolean;
}
